import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import logo from '../../assets/images/M_Multicolor.svg';
import { getDefaultPage } from '../../services/userPreferencesService';
import { ROUTES } from '../../app/routes';
import { markThinkTankNoticePending } from '../notices/ThinkTankNotice';
import '../../styles/AuthPages.css';
export default function ValidationPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [errorState, setErrorState] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    const sourceTool = searchParams.get('sourcetool');
    const isPL = searchParams.get('isPL');
    const isCL = searchParams.get('isCL');
    const insuredParam = searchParams.get('insured');
    const insuredIdParam = searchParams.get('insuredId') || searchParams.get('insuredid');
    const lobParam = searchParams.get('Lob') || searchParams.get('lob');
    const PERSONAL_LOBS = ['PAP', 'HO', 'BUNDLE'];
    const COMMERCIAL_LOBS = ['WC', 'GL', 'BOP', 'CYBER'];
    const ALL_SUPPORTED_LOBS = [...PERSONAL_LOBS, ...COMMERCIAL_LOBS];
    const lobUpper = (lobParam ?? '').toUpperCase();
    useEffect(() => {
        localStorage.clear();
        if (sourceTool === 'Rate') {
            localStorage.setItem('sourceTool', 'Rate');

            const lobIsPL = PERSONAL_LOBS.includes(lobUpper);
            const lobIsCL = COMMERCIAL_LOBS.includes(lobUpper);

            localStorage.setItem('isPL', String(lobParam ? lobIsPL : isPL === 'true'));
            localStorage.setItem('isCL', String(lobParam ? lobIsCL : isCL === 'true'));
        }
        validateNowcertsToken(token);
    }, []);
    const validateNowcertsToken = (token: any) => {
        setLoading(true);
        setErrorState(false);
        fetch(`${process.env.REACT_APP_Auth_Url}/api/account/validatessotoken?token=${encodeURIComponent(token)}`)
            .then(async (res) => {
            if (res.status === 403) {
                const body = await res.json().catch(() => ({}));
                localStorage.clear();
                navigate('/requestearlyaccess', {
                    replace: true,
                    state: {
                        fullName: body?.fullName ?? '',
                        agencyName: body?.agencyName ?? '',
                        email: body?.email ?? '',
                        agencyId: body?.agencyId,
                        agentId: body?.agentId,
                    },
                });
                return;
            }
            const data = await res.json();
            return data;
        })
            .then(async (data: any) => {
            if (!data)
                return;
            if (!data?.authToken) {
                localStorage.clear();
                setErrorState(true);
                setLoading(false);
                return;
            }
            const amsToken = data.amsToken ?? '';
            const toolboxToken = (process.env.REACT_APP_Auth_Url || '')
                ? await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/account/toolboxtoken?nctoken=${encodeURIComponent(amsToken)}`)
                    .then(r => r.json())
                    .then(t => t?.token ?? '')
                    .catch(() => '')
                : '';
            localStorage.setItem('authToken', data.authToken ?? '');
            localStorage.setItem('userEmail', (data.workEmail ?? data.email ?? data.userName ?? '').toLowerCase());
            localStorage.setItem('amsToken', amsToken);
            localStorage.setItem('userName', data.userName ?? '');
            if (data.userId != null)
                localStorage.setItem('userId', String(data.userId));
            localStorage.setItem('agency', data.agencyName ?? '');
            localStorage.setItem('NowcertsAgentId', data.nowCertsAgentId ?? '');
            localStorage.setItem('NCAgencyId', data.nowCertsAgencyId ?? '');
            localStorage.setItem('isAMSUser', String(data.isAMSUser ?? false));
            localStorage.setItem('isSuperUser', String(data.isSuperUser ?? false));
            localStorage.setItem('isAgencyOwner', String(data.isAgencyOwner ?? false));
            localStorage.setItem('showWelcomeChannel', String(data.showWelcomeChannel ?? true));
            localStorage.setItem('nowCertsUserId', data.nowCertsUserId ?? '');
            if (data.agencyDomain)
                localStorage.setItem('agencyDomain', data.agencyDomain);
            localStorage.setItem('firstName', data.firstName ?? '');
            localStorage.setItem('lastName', data.lastName ?? '');
            if (toolboxToken)
                localStorage.setItem('toolboxToken', toolboxToken);
            if (data.themeMode === 1 || data.themeMode === 2) {
                localStorage.setItem('momentum-theme', data.themeMode === 1 ? 'dark' : 'light');
            }
            markThinkTankNoticePending();
            window.dispatchEvent(new CustomEvent('authTokenUpdated'));
            const redirectTarget = sessionStorage.getItem('redirectAfterLogin');
            if (redirectTarget) {
                sessionStorage.removeItem('redirectAfterLogin');
                window.location.href = redirectTarget;
                return;
            }
            if (sourceTool === 'Rate') {
               localStorage.setItem('momentum-theme', 'light');
                const isCLOnly = lobParam
                    ? COMMERCIAL_LOBS.includes(lobUpper)
                    : (isCL === 'true' && isPL !== 'true');
                const basePath = isCLOnly ? ROUTES.COMMERCIAL_LINES_RATING : ROUTES.RATE_QUOTING;

                // Pass auto-quote params via URL
                const aq = new URLSearchParams();
                if (insuredParam && lobParam && ALL_SUPPORTED_LOBS.includes(lobUpper)) {
                    aq.set('autoQuote_insured', insuredParam);
                    aq.set('autoQuote_lob', lobUpper);
                    if (insuredIdParam) aq.set('autoQuote_insuredId', insuredIdParam);
                }
                const qs = aq.toString();
                window.location.href = qs ? `${basePath}?${qs}` : basePath;
                return;
            }
            const defaultPath = await getDefaultPage();
            localStorage.setItem('postLoginDefaultPath', defaultPath || '');
            window.location.href = defaultPath || ROUTES.START;
        })
            .catch(() => {
            setLoading(false);
            setErrorState(true);
            localStorage.clear();
            navigate('/login');
        });
    };
    return (<div className="auth-page">
      <div className="auth-page__glow"/>

      <motion.div className="auth-card-signin glass-elevated" initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}>
        <div className="auth-card__header">
          <img src={logo} alt="Insurance Agency AI" className="auth-card__logo"/>

          <SafeAnimatePresence mode="wait">
            {loading ? (<motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <h1 className="auth-card__title">Authenticating…</h1>
                <p className="auth-card__subtitle">Please wait while we verify your access</p>
              </motion.div>) : errorState ? (<motion.div key="error" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                <h1 className="auth-card__title">Access Denied</h1>
                <p className="auth-card__subtitle">We couldn't verify your credentials.<br />Please try signing in again.</p>
              </motion.div>) : null}
          </SafeAnimatePresence>
        </div>

        <div className="validation-page__status">
          <SafeAnimatePresence mode="wait">
            {loading ? (<motion.span key="spinner" className="auth-card__loader" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.2 }}/>) : errorState ? (<motion.div key="error-icon" className="validation-page__error-icon" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25, ease: [0.34, 1.56, 0.64, 1] }}>
                <FiAlertTriangle size={32}/>
              </motion.div>) : null}
          </SafeAnimatePresence>
        </div>
      </motion.div>
    </div>);
}
